<template>
	<NavBar />
	<BackgroundImage
		:imageUrl="require('@/assets/login/bg.png')"
		:customStyle="{ minHeight: '100vh' }"
	>
		<div class="xiaofu-wrapper">
			<a-spin :spinning="pageLoading">
				<div class="layout">
					<div class="info-column">
						<a-card class="info-card" :bordered="false">
							<div class="info-header">
								<img :src="moduleInfo.avatar" :alt="moduleInfo.name" class="info-avatar" />
								<div class="info-meta">
									<h1 class="info-name">{{ moduleInfo.name }}</h1>
									<p class="info-subtitle">专病智能体 · 高血压管理助手</p>
								</div>
							</div>

							<div class="tag-list">
								<a-tag v-for="tag in moduleInfo.tags" :key="tag.tag_id" color="blue">{{ tag.name }}</a-tag>
							</div>

							<div class="section-tabs">
								<button
									class="tab-btn"
									:class="{ active: activeTab === 'intro' }"
									type="button"
									@click="activeTab = 'intro'"
								>
									功能简介
								</button>
								<button
									class="tab-btn"
									:class="{ active: activeTab === 'summary' }"
									type="button"
									@click="activeTab = 'summary'"
								>
									模型说明
								</button>
							</div>

							<p class="info-description">{{ currentDescription }}</p>

											<div class="more-info">
												<span>了解更多高血压模型信息</span>
												<RightOutlined />
											</div>
						</a-card>

						<a-card class="blood-card" :bordered="false">
							<div class="blood-content">
								<div>
									<h3>高血压打卡</h3>
									<p>记录血压，获得更精准的健康建议</p>
								</div>
								<a-button
									:type="hasRecentRecord ? 'default' : 'primary'"
									:class="{ 'completed-btn': hasRecentRecord }"
									shape="round"
									@click="openBloodPressureModal"
								>
									{{ hasRecentRecord ? '✅ 3日内已打卡' : '立即打卡' }}
								</a-button>
							</div>
						</a-card>

						<a-card class="question-card" :bordered="false">
							<div class="question-header">
								<h3>您可能还想问</h3>
								<p>一键带入对话，获得个性化答复</p>
							</div>
							<div class="question-list">
								<a-button
									v-for="item in quickQuestions"
									:key="item"
									class="question-item"
									block
									shape="round"
									@click="handleQuestionClick(item)"
								>
									<template #icon>
										<MessageOutlined />
									</template>
									{{ item }}
								</a-button>
							</div>
						</a-card>
					</div>

					<div class="chat-column">
						<a-card class="chat-card" :bordered="false">
							<div class="chat-header">
								<div>
									<h2>高血压专病对话</h2>
									<p>围绕血压管理、用药方案与生活方式提供实时建议</p>
								</div>
								<a-tag color="purple">AI 医疗助手</a-tag>
							</div>

							<div ref="chatBody" class="chat-body">
								<div v-if="!chatMessages.length && !chatLoading" class="chat-empty">
									<p>开始提问，AI 将结合您的血压记录提供建议。</p>
									<a-button type="link" @click="handleQuestionClick(quickQuestions[0])">试试快速提问</a-button>
								</div>

								<div
									v-for="(item, index) in chatMessages"
									:key="index"
									:class="['chat-message', item.role]"
								>
									<div class="bubble">
										<p class="chat-text">{{ item.content }}</p>
										<p v-if="item.reasoning" class="chat-reason">{{ item.reasoning }}</p>
										<div v-if="item.loading" class="chat-loading">
											<a-spin size="small" />
											<span>AI 正在思考...</span>
										</div>
										<div v-if="item.error" class="chat-error">未获取到内容，请稍后重试。</div>
									</div>
								</div>
							</div>

							<div class="chat-input">
								<a-textarea
									v-model:value="chatInput"
									:auto-size="{ minRows: 2, maxRows: 4 }"
									placeholder="请输入您想咨询的问题，支持换行"
									:disabled="chatLoading"
									@pressEnter="handlePressEnter"
								/>
								<div class="chat-actions">
									<a-button shape="round" @click="openBloodPressureModal" v-if="!hasRecentRecord">
										去完成打卡
									</a-button>
									<a-button type="primary" shape="round" :loading="chatLoading" @click="handleSend">
										发送
									</a-button>
								</div>
								<p class="chat-tip">内容由 AI 生成，仅供参考</p>
                                <p class="chat-tip">依托华东理工大学信息科学与工程院丁炜超课题组技术支撑</p>
							</div>
						</a-card>
					</div>
				</div>
			</a-spin>
		</div>

		<a-modal
			v-model:open="bloodPressureModalVisible"
			:confirm-loading="submittingClockIn"
			title="高血压打卡"
			ok-text="提交"
			cancel-text="取消"
			@ok="submitBloodPressure"
			@cancel="closeBloodPressureModal"
		>
			<a-form layout="vertical">
				<a-form-item label="收缩压 (mmHg)">
					<a-input-number
						v-model:value="systolicPressure"
						:min="50"
						:max="250"
						:step="1"
						style="width: 100%"
						placeholder="请输入收缩压"
					/>
				</a-form-item>
				<a-form-item label="舒张压 (mmHg)">
					<a-input-number
						v-model:value="diastolicPressure"
						:min="30"
						:max="150"
						:step="1"
						style="width: 100%"
						placeholder="请输入舒张压"
					/>
				</a-form-item>
				<a-alert
					message="建议每日定时测量血压，保持连续记录"
					type="info"
					show-icon
				/>
			</a-form>
		</a-modal>
	</BackgroundImage>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import BackgroundImage from '@/components/BackgroundImage.vue'
import { MessageOutlined, RightOutlined } from '@ant-design/icons-vue'
import api from '@/api'
import { message } from 'ant-design-vue'
// import { XStream } from 'ant-design-x-vue'

const DEFAULT_AVATAR = 'https://qihuang.oss-cn-qingdao.aliyuncs.com/images/doctor/default.png'

export default {
	name: 'XiaofuPage',
	components: {
		NavBar,
		BackgroundImage,
			MessageOutlined,
			RightOutlined
	},
	data() {
		return {
			pageLoading: true,
			moduleInfo: {
				id: 3,
				name: '高血压专病模型',
				avatar: DEFAULT_AVATAR,
				tags: [],
				intro: '高血压健康管理专病模型，提供血压监测、用药提醒与生活方式指导。',
				summary: '模型结合血压记录与中西医知识，为高血压患者提供综合管理建议。'
			},
			activeTab: 'intro',
			hasRecentRecord: true,
			bloodPressureModalVisible: false,
			systolicPressure: null,
			diastolicPressure: null,
			submittingClockIn: false,
			chatMessages: [],
			chatInput: '',
			chatLoading: false,
			sessionId: '0',
			quickQuestions: [
				'高血压患者日常需要注意哪些饮食禁忌？',
				'血压控制不理想时应该如何调整用药？',
				'高血压并发症有哪些，如何预防？'
			]
		}
	},
	computed: {
		currentDescription() {
			return this.activeTab === 'intro' ? this.moduleInfo.intro : this.moduleInfo.summary
		}
	},
	mounted() {
		this.initPage()
	},
	methods: {
		async initPage() {
			try {
				await Promise.all([this.fetchModuleDetail(), this.refreshRecordStatus()])
			} catch (error) {
				console.error('初始化失败:', error)
				message.error('加载小夫助手信息失败，请稍后再试')
			} finally {
				this.pageLoading = false
			}
		},
		async fetchModuleDetail() {
			try {
				const response = await api.xiaofu.getModuleDetail({ doctor_id: this.$route.query.id })
				if (response?.code === 1 && response.data) {
					const data = response.data
					this.moduleInfo = {
						id: data.doctor_id || this.moduleInfo.id,
						name: data.name || this.moduleInfo.name,
						avatar: data.more?.thumbnail || DEFAULT_AVATAR,
						tags: Array.isArray(data.tag) ? data.tag : [],
						intro: data.bio || this.moduleInfo.intro,
						summary: data.expertise || this.moduleInfo.summary
					}
				}
			} catch (error) {
				console.error('获取模型详情失败:', error)
			}
		},
		async refreshRecordStatus() {
			try {
					const response = await api.xiaofu.checkRecentRecord({})
					if (response?.code === 1) {
						this.hasRecentRecord = !!response.data
					} else {
						this.hasRecentRecord = false
					}
			} catch (error) {
				console.error('检查血压打卡状态失败:', error)
				this.hasRecentRecord = false
			}
		},
		handleQuestionClick(question) {
			if (!this.hasRecentRecord) {
				message.warning('请先完成血压打卡后再提问')
				this.openBloodPressureModal()
				return
			}
			this.handleSend(question)
		},
		handleSend(content) {
			const text = typeof content === 'string' ? content.trim() : this.chatInput.trim()
			if (!text) {
				return
			}
			if (!this.hasRecentRecord) {
				message.warning('请先完成血压打卡后再提问')
				this.openBloodPressureModal()
				return
			}
			if (this.chatLoading) {
				message.info('AI 正在回答，请稍候...')
				return
			}
			this.chatInput = ''
			this.pushUserMessage(text)
			this.streamAssistantReply(text)
		},
		handlePressEnter(event) {
			if (!event.shiftKey) {
				event.preventDefault()
				this.handleSend()
			}
		},
		pushUserMessage(content) {
			this.chatMessages.push({
				role: 'user',
				content
			})
			this.$nextTick(this.scrollChatToBottom)
		},
		async streamAssistantReply(content) {
			const assistantMessage = {
				role: 'assistant',
				content: '',
				reasoning: '',
				loading: true,
				error: false
			}
			this.chatMessages.push(assistantMessage)
			this.chatLoading = true
			this.$nextTick(this.scrollChatToBottom)

			try {
				const response = await api.xiaofu.chat({
					user_input: content,
					topic_id: this.sessionId,
					doctor_id: this.moduleInfo.id
				})

				console.log('response', response)

				if (response?.code === 1 && response.data) {
					// 处理成功响应
					if (response.data.response_content) {
						assistantMessage.content = response.data.response_content
					}
					if (response.data.reasoning_content) {
						assistantMessage.reasoning = response.data.reasoning_content
					}
					if (response.data.topic_id || response.data.id) {
						this.sessionId = `${response.data.topic_id || response.data.id}`
					}
					assistantMessage.loading = false
				} else {
					// 处理失败响应
					assistantMessage.error = true
					assistantMessage.content = response?.msg || '请求失败'
					message.error(response?.msg || '请求失败，请稍后再试')
					assistantMessage.loading = false
				}
			} catch (error) {
				console.error('对话失败:', error)
				assistantMessage.error = true
				assistantMessage.loading = false
				assistantMessage.content = '对话请求失败，请稍后再试'
				message.error('对话失败，请稍后再试')
			} finally {
				this.chatLoading = false
				this.$nextTick(this.scrollChatToBottom)
			}
		},
		scrollChatToBottom() {
			const container = this.$refs.chatBody
			if (container) {
				container.scrollTop = container.scrollHeight
			}
		},
		openBloodPressureModal() {
			this.bloodPressureModalVisible = true
		},
		closeBloodPressureModal() {
			this.bloodPressureModalVisible = false
		},
		async submitBloodPressure() {
			if (this.systolicPressure == null || this.diastolicPressure == null) {
				message.warning('请填写完整的血压数据')
				return
			}

			const systolic = Number(this.systolicPressure)
			const diastolic = Number(this.diastolicPressure)

			if (Number.isNaN(systolic) || Number.isNaN(diastolic)) {
				message.error('请输入有效的血压数值')
				return
			}

			if (systolic < 50 || systolic > 250 || diastolic < 30 || diastolic > 150) {
				message.error('血压数值超出合理范围，请重新输入')
				return
			}

			try {
				this.submittingClockIn = true
                const response = await api.xiaofu.bloodClockIn({ systolic, diastolic })
                if (response?.code === 1) {
                    message.success('血压打卡成功')
                    this.hasRecentRecord = true
                    this.closeBloodPressureModal()
                } else {
                    throw new Error(response?.msg || '提交失败')
                }
			} catch (error) {
				console.error('血压打卡失败:', error)
				message.error('血压打卡失败，请稍后再试')
			} finally {
				this.submittingClockIn = false
			}
		}
	}
}
</script>

<style scoped lang="scss">
.xiaofu-wrapper {
	max-width: 1280px;
	margin: 0 auto;
	padding: 96px 20px 60px;
}

.layout {
	display: grid;
	grid-template-columns: 380px 1fr;
	gap: 32px;
	align-items: start;
}

.info-card,
.blood-card,
.question-card,
.chat-card {
	border-radius: 20px;
	border: 1.5px solid rgba(255, 255, 255, 0.3);
	background: rgba(255, 255, 255, 0.92);
	backdrop-filter: blur(18px);
	box-shadow: 0 18px 42px rgba(16, 65, 181, 0.18);
}

.info-card {
	padding: 28px;
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.info-header {
	display: flex;
	align-items: center;
	gap: 18px;
}

.info-avatar {
	width: 92px;
	height: 92px;
	border-radius: 50%;
	object-fit: cover;
	border: 4px solid rgba(94, 123, 243, 0.3);
	box-shadow: 0 10px 24px rgba(94, 123, 243, 0.35);
}

.info-meta {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.info-name {
	margin: 0;
	font-size: 26px;
	font-weight: 700;
	color: #1041b5;
}

.info-subtitle {
	margin: 0;
	font-size: 14px;
	color: #5e7bf3;
}

.tag-list {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin: 4px 0;
}

.section-tabs {
	display: flex;
	gap: 12px;
	padding-top: 8px;
	border-top: 1px solid rgba(94, 123, 243, 0.15);
}

.tab-btn {
	flex: 1;
	padding: 10px 12px;
	border: none;
	border-radius: 14px;
	background: rgba(94, 123, 243, 0.1);
	color: #4b5c8f;
	font-size: 14px;
	transition: all 0.2s ease;
	cursor: pointer;
}

.tab-btn.active {
	background: linear-gradient(135deg, #5e7bf3 0%, #1041b5 100%);
	color: #fff;
	box-shadow: 0 10px 28px rgba(94, 123, 243, 0.35);
}

.info-description {
	margin: 8px;
	font-size: 15px;
	line-height: 1.8;
	color: #4b5c8f;
}

.more-info {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 18px;
	border-radius: 16px;
	background: rgba(94, 123, 243, 0.14);
	color: #1041b5;
	font-size: 14px;
}

.blood-card {
	margin-top: 20px;
	padding: 24px;
}

.blood-content {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
}

.blood-content h3 {
	margin: 0 0 6px;
	font-size: 20px;
	color: #1041b5;
}

.blood-content p {
	margin: 0;
	font-size: 14px;
	color: #5b6a92;
}

.completed-btn {
	background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%) !important;
	border-color: #52c41a !important;
	color: #fff !important;
	font-weight: 600;
	
	&:hover {
		background: linear-gradient(135deg, #73d13d 0%, #52c41a 100%) !important;
		border-color: #73d13d !important;
		box-shadow: 0 4px 12px rgba(82, 196, 26, 0.35);
	}
}

.question-card {
	margin-top: 20px;
	padding: 24px;
}

.question-header h3 {
	margin: 0 0 6px;
	font-size: 18px;
	color: #1041b5;
}

.question-header p {
	margin: 0 0 18px;
	font-size: 13px;
	color: #5b6a92;
}

.question-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.question-item {
	justify-content: flex-start;
	border: 1px solid rgba(94, 123, 243, 0.3);
	background: linear-gradient(90deg, rgba(149, 142, 246, 0.12) 0%, rgba(94, 123, 243, 0.12) 100%);
	color: #1041b5;
}

.chat-card {
	padding: 28px;
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.chat-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
}

.chat-header h2 {
	margin: 0;
	font-size: 24px;
	color: #1041b5;
}

.chat-header p {
	margin: 8px 0 0;
	font-size: 14px;
	color: #5b6a92;
}

.chat-body {
	min-height: 420px;
	max-height: 560px;
	padding-right: 6px;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.chat-empty {
	text-align: center;
	color: #5b6a92;
	padding: 120px 20px;
}

.chat-message {
	display: flex;
}

.chat-message.user {
	justify-content: flex-end;
}

.chat-message.assistant {
	justify-content: flex-start;
}

.bubble {
	max-width: 80%;
	padding: 14px 18px;
	border-radius: 16px;
	background: rgba(94, 123, 243, 0.08);
	color: #2d3a5f;
	box-shadow: 0 10px 28px rgba(94, 123, 243, 0.15);
	line-height: 1.7;
	position: relative;
}

.chat-message.user .bubble {
	background: linear-gradient(135deg, #5e7bf3 0%, #1041b5 100%);
	color: #fff;
}

.chat-text {
	margin: 0;
	white-space: pre-wrap;
}

.chat-reason {
	margin: 12px 0 0;
	font-size: 12px;
	color: rgba(16, 65, 181, 0.75);
}

.chat-loading {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 12px;
	font-size: 13px;
	color: #5b6a92;
}

.chat-error {
	margin-top: 12px;
	font-size: 13px;
	color: #ff4d4f;
}

.chat-input {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.chat-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
}

.chat-tip {
	margin: 0;
	font-size: 12px;
	color: #6c7cab;
	text-align: right;
}

@media (max-width: 1100px) {
	.layout {
		grid-template-columns: 1fr;
	}

	.chat-body {
		max-height: none;
		min-height: 380px;
	}

	.bubble {
		max-width: 100%;
	}
}

@media (max-width: 768px) {
	.xiaofu-wrapper {
		padding: 80px 16px 40px;
	}

	.info-card,
	.blood-card,
	.question-card,
	.chat-card {
		padding: 24px;
	}

	.info-header {
		flex-direction: column;
		align-items: center;
		text-align: center;
	}

	.tag-list {
		justify-content: center;
	}

	.section-tabs {
		flex-direction: column;
	}

	.chat-actions {
		flex-direction: column;
		align-items: stretch;
	}

	.chat-actions .ant-btn {
		width: 100%;
	}

	.chat-tip {
		text-align: center;
	}
}

:deep(.ant-card-body) {
	padding: 0;
}

:deep(.ant-tag) {
	margin-right: 0;
}
</style>
